import { useSyncExternalStore } from 'react';

/**
 * State for dockable side panels — panels the user can minimize, move to the other edge, or float as a
 * window.
 *
 * Two kinds of state, kept apart on purpose:
 *  - **Placement** (`PanelPlacement`) is a preference, persisted per panel key in localStorage, so the
 *    panel reopens where the user last put it.
 *  - **Minimized** is a session gesture, kept in memory only. Persisting it would reopen a panel as a
 *    pill after a reload, which reads as "the button did nothing".
 *
 * Plus the master switch (`Layout.panelDock`) toggled from My profile: off means every dockable panel
 * behaves exactly like a plain side panel.
 *
 * Same shape as menuAttentionStore: the switch lives on the profile page while the readers are panels
 * mounted above the router, so the change has to be broadcast rather than read on mount.
 */

export type PanelDockSide = 'left' | 'right';

export interface PanelPlacement {
  mode: 'dock' | 'float';
  /** The edge it is docked to — while floating, the edge it came from (and where its pill goes). */
  side: PanelDockSide;
  // Floating geometry in viewport px. Kept after re-docking, so floating again returns to the same spot.
  x?: number;
  y?: number;
  w?: number;
  h?: number;
}

/** Floating window size, from the plan: compact enough that the page stays visible around it. */
export const FLOAT_DEFAULT_SIZE = { w: 420, h: 520 };
export const FLOAT_MIN_SIZE = { w: 320, h: 360 };

export const DEFAULT_PANEL_PLACEMENT: PanelPlacement = { mode: 'dock', side: 'right' };

export const PANEL_DOCK_ENABLED_STORAGE_KEY = 'Layout.panelDock';
const PLACEMENT_STORAGE_PREFIX = 'Layout.panelDock.';
const CHANGE_EVENT = 'panel-dock-changed';

const notify = () => window.dispatchEvent(new CustomEvent(CHANGE_EVENT));

const subscribe = (cb: () => void): (() => void) => {
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(PANEL_DOCK_ENABLED_STORAGE_KEY)) cb();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, cb);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, cb);
  };
};

// --- Master switch ------------------------------------------------------------------------------

export const readPanelDockEnabled = (): boolean => {
  try {
    // Unset means on — only an explicit '0' turns it off.
    return localStorage.getItem(PANEL_DOCK_ENABLED_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
};

export const writePanelDockEnabled = (v: boolean) => {
  try {
    localStorage.setItem(PANEL_DOCK_ENABLED_STORAGE_KEY, v ? '1' : '0');
  } catch {
    // ignore
  }
  notify();
};

export const usePanelDockEnabled = (): boolean => useSyncExternalStore(subscribe, readPanelDockEnabled, () => true);

// --- Placement (persisted) ----------------------------------------------------------------------

// useSyncExternalStore needs a stable snapshot, so the parsed object is cached against the raw string
// it came from and handed back unchanged until the stored value actually changes.
const placementCache = new Map<string, { raw: string | null; value: PanelPlacement }>();

export const readPanelPlacement = (key: string): PanelPlacement => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PLACEMENT_STORAGE_PREFIX + key);
  } catch {
    // ignore — falls through to the default
  }
  const cached = placementCache.get(key);
  if (cached && cached.raw === raw) return cached.value;

  let value = DEFAULT_PANEL_PLACEMENT;
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      // Anything unrecognised (an older shape, a hand-edited value) falls back to the default.
      if ((parsed?.mode === 'dock' || parsed?.mode === 'float') && (parsed?.side === 'left' || parsed?.side === 'right')) value = parsed;
    } catch {
      // ignore
    }
  }
  placementCache.set(key, { raw, value });
  return value;
};

export const writePanelPlacement = (key: string, placement: PanelPlacement) => {
  try {
    localStorage.setItem(PLACEMENT_STORAGE_PREFIX + key, JSON.stringify(placement));
  } catch {
    // ignore
  }
  notify();
};

/**
 * Forgets where every panel was put — side, floating position and size — so each opens docked right at
 * its default size again. The profile page's reset action; the master switch is left as it is.
 */
export const resetPanelPlacements = () => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PLACEMENT_STORAGE_PREFIX)) keys.push(k);
    }
    keys.forEach(k => localStorage.removeItem(k));
  } catch {
    // ignore
  }
  notify();
};

export const usePanelPlacement = (key: string): PanelPlacement =>
  useSyncExternalStore(
    subscribe,
    () => readPanelPlacement(key),
    () => DEFAULT_PANEL_PLACEMENT,
  );

// --- Minimized (session only) -------------------------------------------------------------------

// Key → the edge its pill sits on. A Map rather than a Set so the pills can stack per edge, in the
// order the panels were minimized (Maps iterate in insertion order).
const minimizedPanels = new Map<string, PanelDockSide>();

export const isPanelMinimized = (key: string): boolean => minimizedPanels.has(key);

export const setPanelMinimized = (key: string, minimized: boolean, side: PanelDockSide = 'right') => {
  if (minimized === minimizedPanels.has(key)) return;
  if (minimized) minimizedPanels.set(key, side);
  else minimizedPanels.delete(key);
  notify();
};

export const usePanelMinimized = (key: string): boolean =>
  useSyncExternalStore(
    subscribe,
    () => isPanelMinimized(key),
    () => false,
  );

/**
 * A minimized panel's place among the pills on its edge, oldest first — 0 sits in the corner, the next
 * one above it. Several panels can be minimized at once (the assistant plus a half-filled form), and
 * without this their pills would sit on top of each other.
 */
export const minimizedPillIndex = (key: string): number => {
  const side = minimizedPanels.get(key);
  let index = 0;
  for (const [k, s] of minimizedPanels) {
    if (k === key) return index;
    if (s === side) index++;
  }
  return 0;
};

export const useMinimizedPillIndex = (key: string): number =>
  useSyncExternalStore(
    subscribe,
    () => minimizedPillIndex(key),
    () => 0,
  );
